import {useTheme} from "styled-components";
import {useMessages} from "i18n";
import {templateAssetRange, type BookableTemplateKey} from "./sponsorBooking";
import type {SponsorAssetUploaded} from "./SponsorApi";
import {previewBackdropArt, type PreviewVideoArt} from "./takeover/backdropArt";
import {SponsorTakeoverArt} from "./takeover/SponsorTakeover";
import {TemplatePreviewLauncher} from "./TemplatePreviewLauncher";
import {TemplateOption} from "./TemplatePreviewLauncher.styles";
import {
	AssetHint,
	HiddenRadio,
	PickerGrid,
	PreviewBadge,
	PreviewStage,
	placeholderArtUrls,
	TemplateCard,
	TemplateDescription,
	TemplateName,
} from "./BackdropTemplatePicker.styles";

/**
 * One CATEGORY of treatments a sponsor can buy, each previewed by the REAL renderer (ui#496).
 *
 * The list is a PROP since kitecorp/transferify-ui#1868: the catalogue has two categories now, a
 * backdrop that animates behind the download card and a wallpaper that takes the whole page, and those
 * are different things to buy rather than ten tiles in a run. `SponsorPage` mounts one of these per
 * category with the same radio `name`, so the browser still treats all of them as one exclusive choice
 * and arrows between them, while each group carries its own heading and accessible name. The count is
 * never restated in prose here — it has been wrong once already, having said "three" through two
 * separate widenings before ui#624 added the coverflow.
 *
 * The preview mounts {@link SponsorTakeoverArt} — the very component the download page mounts — inside
 * a small clipped stage, fed synthetic art from {@link previewBackdropArt}. That is the entire point: a
 * second, "preview-only" animation implementation would be a promise the product does not keep, and it
 * would drift from the real one on the first change to either. What the sponsor watches here is what a
 * recipient sees.
 *
 * Before any upload the stage is fed token-derived gradient art, so the motion — the thing actually
 * being chosen — is legible from the first paint rather than after a round trip.
 *
 * `drive="sweep"` is what makes that true of the HERO tile too (ui#540). That treatment is scroll-linked
 * on a recipient page, and a 290px card inside a long booking form gives it nothing to read: its progress
 * sits frozen and the sponsor is shown a still image of the one template sold on movement. The sweep
 * feeds the identical transforms from a self-playing clock instead — a different source, not a second
 * animation. Every mount here also stops animating once it scrolls out of view or the tab is
 * backgrounded, which is decided inside {@link SponsorTakeoverArt}, per card.
 *
 * The VIDEO_LOOP tile plays the advertiser's own video once they have uploaded one, and shows its
 * poster frame until then (kitecorp/transferify-ui#1867). The pair is offered to every tile and kept
 * only by the one that draws it — `previewBackdropArt` decides that — so this component has one code
 * path for ten treatments rather than a branch for the odd one out.
 */

export interface BackdropTemplatePickerProps {
	value: BookableTemplateKey;
	onChange: (template: BookableTemplateKey) => void;
	/** The treatments this group offers, in catalogue order. */
	templates: readonly BookableTemplateKey[];
	/** The sponsor's own uploads, previewed as soon as there are any. */
	assets: readonly SponsorAssetUploaded[];
	/**
	 * The VIDEO_LOOP creative, for the one tile that draws it (kitecorp/transferify-ui#1867).
	 *
	 * Passed to the group rather than to a tile because a category is what this component renders and it
	 * has no reason to know which treatment inside it is the video one. Every tile is handed the pair and
	 * every tile but one drops it, which is the same arrangement `assets` already has.
	 */
	video: PreviewVideoArt;
	/** Accessible name for the radio group — the category's own name. */
	groupLabel: string;
	/**
	 * The radio group's form name.
	 *
	 * Shared across every category on a page ON PURPOSE: same-name radios are one exclusive group to the
	 * browser however they are nested, so a sponsor arrows from the last backdrop into the first
	 * wallpaper and choosing one clears the other. Two names would let a sponsor select a treatment in
	 * each category at once.
	 */
	name: string;
}

export function BackdropTemplatePicker(
	{value, onChange, templates, assets, video, groupLabel, name}: BackdropTemplatePickerProps,
) {
	const m = useMessages().sponsor.templates;
	const {tokens} = useTheme();
	const placeholders = placeholderArtUrls(tokens);
	const uploaded = assets.map(asset => asset.url);

	return (
		<PickerGrid role="radiogroup" aria-label={groupLabel}>
			{templates.map(template => {
				const range = templateAssetRange(template);
				const copy = m[template];
				/*
				 * No `options`: a tile sells the MOTION, and nothing has been configured at the moment a
				 * treatment is being chosen. The live composition, with the settings the advertiser picks, is
				 * `WallpaperPreview` beneath the picker.
				 */
				const art = previewBackdropArt(template, uploaded, placeholders, undefined, video);
				return (
					// The card is a label, so a click anywhere in it selects; the view-larger control is
					// its SIBLING rather than its descendant so that a click on it means one thing only
					// (ui#588, owner: "do NOT overload tile click").
					<TemplateOption key={template}>
						<TemplateCard $selected={template === value}>
							<HiddenRadio
								type="radio"
								name={name}
								// Named explicitly (ui#583): the wrapping label is the whole card, so
								// without this the accessible name is the preview badge, the description
								// and the arity hint concatenated — a paragraph per option, where the
								// treatment name is the only part that distinguishes them. The visible
								// name is exactly this string, so the accessible name still contains the
								// visible label (WCAG 2.5.3).
								aria-label={copy.name}
								value={template}
								checked={template === value}
								onChange={() => onChange(template)}
							/>
							<PreviewStage>
								<SponsorTakeoverArt art={art} drive="sweep" stage="tile"/>
								<PreviewBadge>{m.previewLabel}</PreviewBadge>
							</PreviewStage>
							<TemplateName>{copy.name}</TemplateName>
							<TemplateDescription>{copy.description}</TemplateDescription>
							{/* A zero-image treatment has no arity to state; its creative is a video. */}
							{range.max > 0 && <AssetHint>{m.assetHint(range.min, range.max)}</AssetHint>}
						</TemplateCard>
						<TemplatePreviewLauncher
							art={art}
							templateName={copy.name}
							templateDescription={copy.description}
						/>
					</TemplateOption>
				);
			})}
		</PickerGrid>
	);
}
